import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { FileText, Eye, Send, CheckCircle, Clock, AlertCircle, Filter, Search, Mail, Trash2, Download, XCircle, Edit3, RefreshCw, MoreVertical, Calendar, User } from 'lucide-react';
import SendInvoiceModal from './modals/SendInvoiceModal';
import DeleteInvoiceConfirmModal from './modals/DeleteInvoiceConfirmModal';
import CancelInvoiceModal from './modals/CancelInvoiceModal';
import EditInvoiceModal from './modals/EditInvoiceModal';
import stripeBillingService from '../services/stripeBillingService';
import { telechargerFacturePDF } from '../services/invoicePdfService';
import { resendInvoiceEmail, sendCancellationEmail } from '../services/invoiceEmailService';

/**
 * Composant Menu d'actions pour mobile et desktop
 */
const InvoiceActionsMenu = ({ facture, handlers }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    { icon: Eye, label: 'Voir détails', onClick: () => handlers.handleView(facture), color: 'text-blue-600 dark:text-blue-400', show: true },
    { icon: Mail, label: 'Envoyer par email', onClick: () => handlers.handleSend(facture), color: 'text-green-600 dark:text-green-400', show: facture.statut !== 'annulée' },
    { icon: Download, label: 'Télécharger PDF', onClick: () => handlers.handleDownload(facture), color: 'text-purple-600 dark:text-purple-400', show: true },
    { icon: RefreshCw, label: 'Renvoyer l\'email', onClick: () => handlers.handleResend(facture), color: 'text-cyan-600 dark:text-cyan-400', show: facture.emailEnvoye && facture.statut !== 'annulée' },
    { icon: Edit3, label: 'Modifier', onClick: () => handlers.handleEdit(facture), color: 'text-yellow-600 dark:text-yellow-400', show: facture.statut !== 'payée' && facture.statut !== 'annulée', disabled: facture.statut === 'payée' },
    { icon: XCircle, label: 'Annuler', onClick: () => handlers.handleCancel(facture), color: 'text-orange-600 dark:text-orange-400', show: facture.statut !== 'payée' && facture.statut !== 'annulée', disabled: facture.statut === 'payée' },
    { icon: Trash2, label: 'Supprimer', onClick: () => handlers.handleDelete(facture), color: 'text-red-600 dark:text-red-400', show: facture.statut !== 'payée', disabled: facture.statut === 'payée', tooltip: facture.statut === 'payée' ? 'Impossible de supprimer une facture payée' : '' },
  ].filter(action => action.show);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        title="Actions"
      >
        <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
            <div className="py-1">
              {actions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={() => {
                      action.onClick();
                      setIsOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${action.color}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Composant Carte de facture pour mobile
 */
const InvoiceCard = ({ facture, handlers, getStatutBadge, formatMontant, formatDate }) => {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* En-tête de la carte */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono text-gray-500 dark:text-gray-400 mb-1">
            {facture.id}
          </div>
          <div className="flex items-center space-x-2">
            <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {facture.client?.nom || '-'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {facture.client?.email || '-'}
              </div>
            </div>
          </div>
        </div>
        <div className="ml-2">
          {getStatutBadge(facture.statut)}
        </div>
      </div>

      {/* Montant en évidence */}
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 mb-3">
        <div className="text-xs text-green-600 dark:text-green-400 mb-1">Montant total</div>
        <div className="text-2xl font-bold text-green-700 dark:text-green-300">
          {formatMontant(facture.calcul?.montantTotal || 0)}
        </div>
      </div>

      {/* Détails */}
      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
        <div>
          <div className="flex items-center text-gray-500 dark:text-gray-400 mb-1">
            <Calendar className="w-3 h-3 mr-1" />
            <span className="text-xs">Période</span>
          </div>
          <div className="text-gray-900 dark:text-white text-xs truncate">
            {facture.periode?.libelle || '-'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Heures / RDV</div>
          <div className="text-gray-900 dark:text-white text-xs">
            {facture.calcul?.totalHeures || 0}h / {facture.calcul?.nombreRendezVous || 0}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Date génération</div>
          <div className="text-gray-900 dark:text-white text-xs">
            {formatDate(facture.dateGeneration)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Tarif unitaire</div>
          <div className="text-gray-900 dark:text-white text-xs">
            {formatMontant(facture.calcul?.fraisMandatUnitaire || 0)}/h
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex space-x-1">
          <button
            onClick={() => handlers.handleView(facture)}
            className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition-colors"
            title="Voir détails"
          >
            <Eye className="w-4 h-4" />
          </button>
          {facture.statut !== 'annulée' && (
            <button
              onClick={() => handlers.handleSend(facture)}
              className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded transition-colors"
              title="Envoyer par email"
            >
              <Mail className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handlers.handleDownload(facture)}
            className="p-2 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 rounded transition-colors"
            title="Télécharger PDF"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
        <InvoiceActionsMenu facture={facture} handlers={handlers} />
      </div>
    </div>
  );
};

/**
 * Composant de gestion des factures générées
 * Affiche toutes les factures créées via stripeBillingService
 */
const InvoiceManagement = () => {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [recherche, setRecherche] = useState('');
  const [factureSelectionnee, setFactureSelectionnee] = useState(null);

  // États pour les modals
  const [showSendModal, setShowSendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [invoiceForAction, setInvoiceForAction] = useState(null);

  // État pour les notifications toast
  const [toast, setToast] = useState(null);

  useEffect(() => {
    chargerFactures();
  }, []);

  const chargerFactures = async () => {
    try {
      setLoading(true);
      console.log('📋 Chargement des factures depuis stripeBillingInvoices...');

      const facturesRef = collection(db, 'stripeBillingInvoices');

      // Essayer d'abord sans orderBy pour voir si les données existent
      const snapshot = await getDocs(facturesRef);
      console.log(`📊 Nombre de factures trouvées: ${snapshot.docs.length}`);

      const facturesData = snapshot.docs
        .map(doc => {
          const data = doc.data();
          console.log('📄 Facture:', doc.id, data);
          return {
            id: doc.id,
            ...data
          };
        })
        .filter(facture => !facture.deleted); // Filtrer les factures supprimées

      // Trier manuellement par dateGeneration
      facturesData.sort((a, b) => {
        const dateA = a.dateGeneration?.toDate?.() || new Date(0);
        const dateB = b.dateGeneration?.toDate?.() || new Date(0);
        return dateB - dateA; // Plus récent en premier
      });

      console.log('✅ Factures chargées et triées:', facturesData);
      setFactures(facturesData);
    } catch (error) {
      console.error('❌ Erreur chargement factures:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'generée': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-200', icon: Clock },
      'envoyée': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-200', icon: Send },
      'payée': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-200', icon: CheckCircle },
      'en_attente': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-200', icon: Clock },
      'annulée': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-200', icon: AlertCircle }
    };

    const badge = badges[statut] || badges['generée'];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {statut}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant);
  };

  const facturesFiltrees = factures.filter(facture => {
    const matchStatut = filtreStatut === 'tous' || facture.statut === filtreStatut;
    const matchRecherche = recherche === '' ||
      facture.client?.nom?.toLowerCase().includes(recherche.toLowerCase()) ||
      facture.id?.toLowerCase().includes(recherche.toLowerCase());
    return matchStatut && matchRecherche;
  });

  const marquerCommeEnvoyee = async (factureId) => {
    try {
      const factureRef = doc(db, 'stripeBillingInvoices', factureId);
      await updateDoc(factureRef, {
        statut: 'envoyée',
        dateEnvoi: new Date()
      });
      await chargerFactures();
      console.log('Facture marquée comme envoyée');
    } catch (error) {
      console.error('Erreur mise à jour facture:', error);
    }
  };

  const marquerCommePayee = async (factureId) => {
    try {
      const factureRef = doc(db, 'stripeBillingInvoices', factureId);
      await updateDoc(factureRef, {
        statut: 'payée',
        datePaiement: new Date()
      });
      await chargerFactures();
      console.log('Facture marquée comme payée');
    } catch (error) {
      console.error('Erreur mise à jour facture:', error);
    }
  };

  // Fonction pour afficher les notifications toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000); // Masquer après 5 secondes
  };

  // Handler pour envoyer une facture par email
  const handleSendInvoice = (invoice) => {
    setInvoiceForAction(invoice);
    setShowSendModal(true);
  };

  const handleSendSuccess = (result) => {
    showToast('Email envoyé avec succès !', 'success');
    chargerFactures();
  };

  // Handler pour télécharger le PDF
  const handleDownloadPDF = async (invoice) => {
    try {
      showToast('Génération du PDF en cours...', 'info');
      await telechargerFacturePDF(invoice);
      showToast('PDF téléchargé avec succès !', 'success');
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      showToast('Erreur lors du téléchargement du PDF', 'error');
    }
  };

  // Handler pour renvoyer un email
  const handleResendEmail = async (invoice) => {
    try {
      showToast('Renvoi de l\'email en cours...', 'info');
      await resendInvoiceEmail(invoice, invoice.client?.email, invoice.client?.nom);
      showToast('Email renvoyé avec succès !', 'success');
      chargerFactures();
    } catch (error) {
      console.error('Erreur renvoi email:', error);
      showToast('Erreur lors du renvoi de l\'email', 'error');
    }
  };

  // Handler pour supprimer une facture
  const handleDeleteInvoice = (invoice) => {
    setInvoiceForAction(invoice);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (invoice, reason) => {
    try {
      await stripeBillingService.supprimerFacture(invoice.id, reason);
      showToast('Facture supprimée avec succès !', 'success');
      chargerFactures();
    } catch (error) {
      console.error('Erreur suppression facture:', error);
      showToast(error.message || 'Erreur lors de la suppression', 'error');
      throw error;
    }
  };

  // Handler pour annuler une facture
  const handleCancelInvoice = (invoice) => {
    setInvoiceForAction(invoice);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (invoice, reason, notifyClient) => {
    try {
      await stripeBillingService.annulerFacture(invoice.id, reason);

      if (notifyClient && invoice.client?.email) {
        await sendCancellationEmail(invoice, invoice.client.email, invoice.client.nom, reason);
      }

      showToast('Facture annulée avec succès !', 'success');
      chargerFactures();
    } catch (error) {
      console.error('Erreur annulation facture:', error);
      showToast('Erreur lors de l\'annulation', 'error');
      throw error;
    }
  };

  // Handler pour modifier une facture
  const handleEditInvoice = (invoice) => {
    setInvoiceForAction(invoice);
    setShowEditModal(true);
  };

  const handleEditConfirm = async (invoice, updates) => {
    try {
      await stripeBillingService.mettreAJourFacture(invoice.id, updates);
      showToast('Facture modifiée avec succès !', 'success');
      chargerFactures();
    } catch (error) {
      console.error('Erreur modification facture:', error);
      showToast('Erreur lors de la modification', 'error');
      throw error;
    }
  };

  // Objet regroupant tous les handlers pour les passer aux composants
  const handlers = {
    handleView: setFactureSelectionnee,
    handleSend: handleSendInvoice,
    handleDownload: handleDownloadPDF,
    handleResend: handleResendEmail,
    handleEdit: handleEditInvoice,
    handleCancel: handleCancelInvoice,
    handleDelete: handleDeleteInvoice
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Chargement des factures...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6">
            <div className="mb-3 sm:mb-0">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <FileText className="w-6 h-6 md:w-7 md:h-7 mr-2 md:mr-3 text-blue-500" />
                Gestion des factures
              </h2>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
                Toutes les factures générées pour les bénéficiaires
              </p>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400">
                {factures.length}
              </div>
              <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                factures total
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par client ou ID..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filtre par statut */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filtreStatut}
                onChange={(e) => setFiltreStatut(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="tous">Tous les statuts</option>
                <option value="generée">Générée</option>
                <option value="envoyée">Envoyée</option>
                <option value="payée">Payée</option>
                <option value="en_attente">En attente</option>
                <option value="annulée">Annulée</option>
              </select>
            </div>
          </div>

          {/* Statistiques rapides */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-4 md:mt-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 md:p-4 rounded-lg">
              <div className="text-xs md:text-sm text-blue-600 dark:text-blue-400">Générées</div>
              <div className="text-xl md:text-2xl font-bold text-blue-700 dark:text-blue-300">
                {factures.filter(f => f.statut === 'generée').length}
              </div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 md:p-4 rounded-lg">
              <div className="text-xs md:text-sm text-yellow-600 dark:text-yellow-400">Envoyées</div>
              <div className="text-xl md:text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                {factures.filter(f => f.statut === 'envoyée').length}
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-3 md:p-4 rounded-lg">
              <div className="text-xs md:text-sm text-green-600 dark:text-green-400">Payées</div>
              <div className="text-xl md:text-2xl font-bold text-green-700 dark:text-green-300">
                {factures.filter(f => f.statut === 'payée').length}
              </div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 md:p-4 rounded-lg">
              <div className="text-xs md:text-sm text-purple-600 dark:text-purple-400">Montant total</div>
              <div className="text-base md:text-lg font-bold text-purple-700 dark:text-purple-300">
                {formatMontant(factures.reduce((sum, f) => sum + (f.calcul?.montantTotal || 0), 0))}
              </div>
            </div>
          </div>
        </div>

        {/* Liste des factures */}
        {facturesFiltrees.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {recherche || filtreStatut !== 'tous'
                ? 'Aucune facture ne correspond à vos critères'
                : 'Aucune facture générée pour le moment'}
            </p>
          </div>
        ) : (
          <>
            {/* Vue mobile - Cartes */}
            <div className="block md:hidden space-y-3">
              {facturesFiltrees.map((facture) => (
                <InvoiceCard
                  key={facture.id}
                  facture={facture}
                  handlers={handlers}
                  getStatutBadge={getStatutBadge}
                  formatMontant={formatMontant}
                  formatDate={formatDate}
                />
              ))}
            </div>

            {/* Vue desktop - Tableau */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      ID Facture
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Période
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Heures
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {facturesFiltrees.map((facture) => (
                    <tr
                      key={facture.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900 dark:text-white">
                          {facture.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {facture.client?.nom || '-'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {facture.client?.email || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {facture.periode?.libelle || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {facture.calcul?.totalHeures || 0}h
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {facture.calcul?.nombreRendezVous || 0} RDV
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatMontant(facture.calcul?.montantTotal || 0)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatMontant(facture.calcul?.fraisMandatUnitaire || 0)}/h
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatutBadge(facture.statut)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(facture.dateGeneration)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-1">
                          {/* Voir détails */}
                          <button
                            onClick={() => setFactureSelectionnee(facture)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Envoyer par email */}
                          {facture.statut !== 'annulée' && (
                            <button
                              onClick={() => handleSendInvoice(facture)}
                              className="p-1.5 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded transition-colors"
                              title="Envoyer par email"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          )}

                          {/* Télécharger PDF */}
                          <button
                            onClick={() => handleDownloadPDF(facture)}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 rounded transition-colors"
                            title="Télécharger PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>

                          {/* Renvoyer email (si déjà envoyée) */}
                          {facture.emailEnvoye && facture.statut !== 'annulée' && (
                            <button
                              onClick={() => handleResendEmail(facture)}
                              className="p-1.5 text-cyan-600 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-900/20 rounded transition-colors"
                              title="Renvoyer l'email"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}

                          {/* Modifier (si pas payée ni annulée) */}
                          {facture.statut !== 'payée' && facture.statut !== 'annulée' && (
                            <button
                              onClick={() => handleEditInvoice(facture)}
                              className="p-1.5 text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20 rounded transition-colors"
                              title="Modifier la facture"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                          )}

                          {/* Annuler (si pas payée ni annulée) */}
                          {facture.statut !== 'payée' && facture.statut !== 'annulée' && (
                            <button
                              onClick={() => handleCancelInvoice(facture)}
                              className="p-1.5 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20 rounded transition-colors"
                              title="Annuler la facture"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}

                          {/* Supprimer (si pas payée) */}
                          {facture.statut !== 'payée' && (
                            <button
                              onClick={() => handleDeleteInvoice(facture)}
                              className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Supprimer définitivement"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}

                          {/* Message si facture payée (protégée) */}
                          {facture.statut === 'payée' && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 italic" title="Les factures payées sont protégées">
                              🔒 Protégée
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        {/* Modal détails facture */}
        {factureSelectionnee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Détails de la facture
                  </h3>
                  <button
                    onClick={() => setFactureSelectionnee(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                </div>
              </div>

              <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                {/* Informations générales */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Informations générales</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">ID:</span>
                      <div className="font-mono text-gray-900 dark:text-white">{factureSelectionnee.id}</div>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Statut:</span>
                      <div className="mt-1">{getStatutBadge(factureSelectionnee.statut)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Date génération:</span>
                      <div className="text-gray-900 dark:text-white">{formatDate(factureSelectionnee.dateGeneration)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Créé par:</span>
                      <div className="text-gray-900 dark:text-white">{factureSelectionnee.createdBy || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Informations client */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Client</h4>
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-4 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Nom:</span>
                      <span className="text-gray-900 dark:text-white font-medium">{factureSelectionnee.client?.nom}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Email:</span>
                      <span className="text-gray-900 dark:text-white">{factureSelectionnee.client?.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Téléphone:</span>
                      <span className="text-gray-900 dark:text-white">{factureSelectionnee.client?.telephone || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Période */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Période facturée</h4>
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-4 rounded-lg text-sm">
                    <div className="text-lg font-medium text-gray-900 dark:text-white">
                      {factureSelectionnee.periode?.libelle}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400 mt-1">
                      Du {formatDate(factureSelectionnee.periode?.debut)} au {formatDate(factureSelectionnee.periode?.fin)}
                    </div>
                  </div>
                </div>

                {/* Calcul */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Détail du calcul</h4>
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-4 rounded-lg space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Nombre de rendez-vous:</span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {factureSelectionnee.calcul?.nombreRendezVous || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Total heures:</span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {factureSelectionnee.calcul?.totalHeures || 0}h
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Frais mandat unitaire:</span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {formatMontant(factureSelectionnee.calcul?.fraisMandatUnitaire || 0)}/h
                      </span>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex justify-between">
                      <span className="text-gray-900 dark:text-white font-semibold">Montant total:</span>
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {formatMontant(factureSelectionnee.calcul?.montantTotal || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stripe */}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Informations Stripe</h4>
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-4 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Produit ID:</span>
                      <span className="text-gray-900 dark:text-white font-mono text-xs">
                        {factureSelectionnee.stripe?.produitId}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Prix ID:</span>
                      <span className="text-gray-900 dark:text-white font-mono text-xs">
                        {factureSelectionnee.stripe?.prixId}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <button
                  onClick={() => setFactureSelectionnee(null)}
                  className="w-full px-4 py-2 md:py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm md:text-base"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modals d'action */}
        <SendInvoiceModal
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setInvoiceForAction(null);
          }}
          invoice={invoiceForAction}
          onSuccess={handleSendSuccess}
        />

        <DeleteInvoiceConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setInvoiceForAction(null);
          }}
          invoice={invoiceForAction}
          onConfirm={handleDeleteConfirm}
        />

        <CancelInvoiceModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setInvoiceForAction(null);
          }}
          invoice={invoiceForAction}
          onConfirm={handleCancelConfirm}
        />

        <EditInvoiceModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setInvoiceForAction(null);
          }}
          invoice={invoiceForAction}
          onConfirm={handleEditConfirm}
        />

        {/* Toast notification */}
        {toast && (
          <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
            <div className={`rounded-lg shadow-lg p-4 flex items-center space-x-3 ${
              toast.type === 'success' ? 'bg-green-500' :
              toast.type === 'error' ? 'bg-red-500' :
              toast.type === 'info' ? 'bg-blue-500' :
              'bg-gray-500'
            } text-white`}>
              {toast.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {toast.type === 'error' && <AlertCircle className="w-5 h-5" />}
              {toast.type === 'info' && <Clock className="w-5 h-5" />}
              <span className="font-medium">{toast.message}</span>
              <button
                onClick={() => setToast(null)}
                className="ml-4 hover:opacity-80"
              >
                <span className="text-xl">×</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceManagement;
